import React, {
  useEffect,
  useRef,
  useState,
  Suspense,
  lazy,
  useMemo,
} from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { createRoot } from "react-dom/client";
import _merge from "lodash/merge";
import { colorSchemeContext as ColorSchemeContext } from "./context/colorSchemeContext";
import {
  createBrowserRouter,
  RouterProvider,
  matchRoutes,
} from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { lightTheme, darkTheme } from "@caelor/ui";

import { FlagsProvider } from "@atlaskit/flag";

import { initAppInConfluenceContext, loadScript } from "./index";

import { themes } from "./theme-colors";
import { useLanguageDetect } from "./hooks/use-language-detect";
import { MACRO_KEY } from "./optics/defaults";

import OpticsEditor from "./optics/components/editor/Editor";
import DexEditor from "./dex/components/editor/Editor";

const [MacrosuiteConfiguration, StyleguideLanding, StyleguidePermissions] = [
  lazy(() => import("./configuration/components/MacrosuiteConfiguration")),
  lazy(() => import("./styleguide/pages/Landing")),
  lazy(() => import("./styleguide/pages/Permissions")),
];

// Optics Editor has to be imported directly, because of some issue with QuillEditor
// When the component is lazy loaded it throws the error: Property description must be an object: undefined
// This error happens only on production build (npm run build)
// I can't seem to find the reason for this issue, so far it seems like it is inside the QuillEditor library
// Leon
const [
  TooltipFootnote,
  FootnoteDisplay,
  Designer,
  DesignerEditor,
  TabGroup,
  Tab,
  Panel,
  Button,
  Optics,
  Dex,
  // OpticsEditor,
] = [
  lazy(() => import("./features/tooltip-and-footnote/view/TooltipFootnote")),
  lazy(() => import("./features/footnote-display/view/FootnoteDisplayView")),
  lazy(() => import("./features/elementor/Components/ElementorView")),
  lazy(() => import("./features/elementor/Components/ElementorEditor")),
  lazy(() => import("./features/nested-tabs/tab-group/components/View")),
  lazy(() => import("./features/nested-tabs/tab/components/View")),
  lazy(() => import("./features/panel/PanelView")),
  lazy(() => import("./features/button-inline/view/ButtonInlineView")),
  lazy(() => import("./optics/components/view/View")),
  lazy(() => import("./dex/components/view/View")),
  // lazy(() => import("./optics/components/editor/Editor")),
];

const getParameterByName = (name, url = window.location.href) => {
  name = name.replace(/[[\]]/g, "\\$&");
  const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
};

const DEFAULT_COLOR_MODE = "light";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const css = (element, style) => {
  for (const property in style) {
    element.style[property] = style[property];
  }
};

const macro_routes = {
  dex: { View: <Dex />, Edit: <DexEditor /> },
  "tooltip-and-footnote": {
    View: <TooltipFootnote />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.TOOLTIPS_AND_FOOTNOTES} />,
  },
  "footnote-display": {
    View: <FootnoteDisplay />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.FOOTNOTE_DISPLAY} />,
  },
  "title-and-text": {
    View: <Optics macroKey={MACRO_KEY.TEXT_AND_TITLE} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.TEXT_AND_TITLE} />,
  },
  "easy-html": {
    View: <Optics macroKey={MACRO_KEY.HTML} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.HTML} />,
  },
  button: {
    View: <Button />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.BUTTON_INLINE} />,
  },
  optics: { View: <Optics />, Edit: <OpticsEditor /> },
  designer: { View: <Designer />, Edit: <DesignerEditor /> },
  countdown: {
    View: <Optics macroKey={MACRO_KEY.COUNTDOWN} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.COUNTDOWN} />,
  },
  announcement: {
    View: <Optics macroKey={MACRO_KEY.ANNOUNCEMENTS} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.ANNOUNCEMENTS} />,
  },
  "button-block": {
    View: <Optics macroKey={MACRO_KEY.BUTTON} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.BUTTON} />,
  },
  "panel-simple": {
    View: <Optics macroKey={MACRO_KEY.PANEL_SIMPLE} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.PANEL_SIMPLE} />,
  },
  "cards-v2": {
    View: <Optics macroKey={MACRO_KEY.CARDS} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.CARDS} />,
  },
  news: {
    View: <Optics macroKey={MACRO_KEY.NEWS} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.NEWS} />,
  },
  pages: {
    View: <Optics macroKey={MACRO_KEY.PAGES} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.PAGES} />,
  },
  spaces: {
    View: <Optics macroKey={MACRO_KEY.SPACES} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.SPACES} />,
  },
  people: {
    View: <Optics macroKey={MACRO_KEY.PEOPLE} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.PEOPLE} />,
  },
  "org-chart": {
    View: <Optics macroKey={MACRO_KEY.ORG_CHART} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.ORG_CHART} />,
  },
  "recently-active": {
    View: <Optics macroKey={MACRO_KEY.RECENTLY_ACTIVE} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.RECENTLY_ACTIVE} />,
  },
  "upcoming-birthdays": {
    View: <Optics macroKey={MACRO_KEY.UPCOMING_BIRTHDAYS} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.UPCOMING_BIRTHDAYS} />,
  },
  "new-starters": {
    View: <Optics macroKey={MACRO_KEY.NEW_STARTERS} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.NEW_STARTERS} />,
  },
  "work-anniversary": {
    View: <Optics macroKey={MACRO_KEY.WORK_ANNIVERSARY} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.WORK_ANNIVERSARY} />,
  },
  divider: {
    View: <Optics macroKey={MACRO_KEY.SPACER_AND_DIVIDER} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.SPACER_AND_DIVIDER} />,
  },
  "image-slider": {
    View: <Optics macroKey={MACRO_KEY.IMAGE_SLIDER} />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.IMAGE_SLIDER} />,
  },
  tab: {
    View: <Tab />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.ADVANCED_TABS_SINGLE} />,
  },
  "tab-group": {
    View: <TabGroup />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.ADVANCED_TABS_GROUP} />,
  },
  panel: {
    View: <Panel />,
    Edit: <OpticsEditor editorMacro={MACRO_KEY.PANEL_ADVANCED} />,
  },
};

const general_routes = {
  "macrosuite-configuration/configuration": <MacrosuiteConfiguration />,
  "styleguide/permissions": <StyleguidePermissions />,
  styleguide: <StyleguideLanding />,
  "macrosuite-info-banner": (
    <QueryClientProvider client={queryClient}></QueryClientProvider>
  ),
};

const router = createBrowserRouter([
  ...Object.keys(general_routes).map((route) => ({
    path: route,
    element: general_routes[route],
  })),
  ...Object.keys(macro_routes)
    .map((macro) => [
      {
        path: `macro/${macro}`,
        element: <>{macro_routes[macro].View}</>,
      },
      {
        path: `macro/editor/${macro}`,
        element: (
          <QueryClientProvider client={queryClient}>
            {macro_routes[macro].Edit}
          </QueryClientProvider>
        ),
      },
    ])
    .flat(),
]);

const checkIfRoutesMatch = () => {
  const macroRoutes = Object.keys(macro_routes).map((macro) => [
    { path: `macro/${macro}` },
    { path: `macro/editor/${macro}` },
  ]);

  const generalRoutes = Object.keys(general_routes).map((route) => ({
    path: route,
  }));

  const { pathname } = window.location;
  const matches = matchRoutes([...macroRoutes.flat(), ...generalRoutes], {
    pathname,
  });

  return matches?.length;
};

const individualMacroStyles = (macro, element) => {
  if (macro === "designer") {
    css(element, { height: "100vh" });
    css(document.body, {
      margin: "0 !important",
      background: "#fff",
    });
  } else if (macro === "dex") {
    css(element, { height: "100vh" });
  } else {
    const style = document.createElement("style");

    style.textContent = `
      @media (max-width: 1740px) {
        html, body { height: 100%; }

        #content, .ac-content {
          max-width: 100% !important;
          height: 100% !important;
          margin: 0 !important;
        }
      }
    `;

    document.head.appendChild(style);

    css(element, {
      height: "90vh",
      maxWidth: "1700px",
      margin: "5vh auto",
      borderRadius: "20px",
      overflow: "hidden",
      background: "transparent",
    });
  }
};

const DARK_MODE_SUPPORTED = [
  "cards-v2",
  "button",
  "announcement",
  "panel",
  "divider",
  "image-slider",
  "tab-group",
  "tab",
  "title-and-text",
  "permissions",
  "macrosuite-configuration",
  "optics",
  "countdown",
  "tooltip-and-footnote",
  "footnote-display",
  "easy-html",
  "people",
  "recently-active",
  "news",
  "pages",
  "spaces",
  "new-starters",
  "dex",
];

const App = () => {
  const [atlassianConnectLoaded, setAtlassianConnectLoaded] = useState(false);
  const [colorScheme, setColorScheme] = useState(DEFAULT_COLOR_MODE);

  const selectedMacro = useRef("");
  const rootElementRef = useRef(document.documentElement);

  const { dark, light } = themes;

  const mergedThemes = useMemo(() => {
    return {
      dark: _merge({}, dark, darkTheme),
      light: _merge({}, light, lightTheme),
    };
  }, [dark, light]);

  useLanguageDetect();

  useEffect(() => {
    if (selectedMacro.current !== "designer") {
      window.AP?.theming?.initializeTheming();
    }
  }, [window?.AP]);

  useEffect(() => {
    if (
      DARK_MODE_SUPPORTED.find(
        (key) =>
          selectedMacro.current === key || window.location.href.includes(key)
      )
    ) {
      const rootElement = rootElementRef.current;
      const observer = new MutationObserver((mutationsList) => {
        mutationsList.forEach((mutation) => {
          if (mutation.attributeName === "data-color-mode") {
            const colorMode = rootElement.getAttribute("data-color-mode");
            setColorScheme(colorMode || DEFAULT_COLOR_MODE);
          }
        });
      });
      observer.observe(rootElement, { attributes: true });
      if (!rootElement.hasAttribute("data-color-mode")) {
        setColorScheme(DEFAULT_COLOR_MODE);
      }

      return () => observer.disconnect();
    }
  }, [selectedMacro.current]);

  useEffect(() => {
    (async () => {
      selectedMacro.current = getParameterByName("macro");

      if (["panel", "tab-group"].includes(selectedMacro.current)) {
        await loadScript({
          url: "https://cdnjs.cloudflare.com/ajax/libs/jquery/3.4.1/jquery.min.js",
          global: "jQuery",
        });
        await loadScript({
          url: "https://unpkg.com/@atlassian/aui@8.6.0/dist/aui/aui-css-deprecations.js",
        });
        await loadScript({
          url: "https://unpkg.com/@atlassian/aui@8.6.0/dist/aui/aui-prototyping.js",
        });
      }

      const isAtlassianScriptInitialized = await initAppInConfluenceContext();
      const context = await window.AP.context.getContext();
      const element = document.getElementsByClassName("ac-content");
      const invalidLicense = getParameterByName("invalid-license");
      const mode = getParameterByName("mode");

      if (invalidLicense) {
        element[0].classList.remove("hide");
        return setAtlassianConnectLoaded(isAtlassianScriptInitialized);
      }

      if (
        ["display", "preview"].includes(context?.confluence?.macro?.outputType)
      ) {
        element[0].classList.remove("hide");
      }

      if (mode === "custom-macro-edtior") {
        element[0].classList.remove("hide");
        if (element && element[0]) {
          individualMacroStyles(selectedMacro.current, element[0]);
        }
      }
      if (mode === "editor") {
        window.AP.dialog
          .create({
            key: "custom-macro-editor",
            width: "100%",
            height: "100%",
            customData: { macro: selectedMacro.current },
          })
          .on("close", () => {
            window.AP.confluence.closeMacroEditor();
          });
      } else {
        element[0].classList.remove("hide");
      }

      setAtlassianConnectLoaded(isAtlassianScriptInitialized);
    })();
  }, []);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      {atlassianConnectLoaded ? (
        <ColorSchemeContext.Provider value={{ colorScheme, mergedThemes }}>
          <ThemeProvider theme={mergedThemes[colorScheme]}>
            <QueryClientProvider client={queryClient}>
              <FlagsProvider>
                <RouterProvider router={router} />
              </FlagsProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </ColorSchemeContext.Provider>
      ) : (
        <div>Loading...</div>
      )}
    </Suspense>
  );
};

if (checkIfRoutesMatch()) {
  const root = createRoot(document.getElementById("root"));
  root.render(<App />);
} else {
  // Load the legacy build
  const script = document.createElement("script");
  script.src = "/legacy/legacy.js";
  script.type = "text/javascript";
  document.head.appendChild(script);
}
